<template>
  <div v-if="$can('view', 'YachtModel')">
    <v-row justify="center">
      <v-col cols="12">
        <v-card
          v-for="countryFinanceModel in data"
          :key="countryFinanceModel.id"
          class="pa-4 mb-3"
          :ripple="false"
        >
          <v-row
            no-gutters
            style="cursor: pointer"
            @click="countryFinanceModel.is_expanded = !countryFinanceModel.is_expanded"
          >
            <v-col cols="3">
              {{ countryFinanceModel.finance_model.label }}
            </v-col>
            <v-col
              cols="3"
              class="text-body-1"
            >
              <flag
                :iso="countryFinanceModel.country.code"
                :title="countryFinanceModel.country.name"
                :squared="false"
              />
              {{ countryFinanceModel.country.name }}
            </v-col>
            <v-spacer />
            <v-col cols="auto">
              <v-switch
                v-model="countryFinanceModel.is_sales_available"
                class="pa-0 ma-0"
                dense
                label="Sales available"
                hide-details
                @click.prevent.stop
              />
            </v-col>
            <v-col
              cols="auto"
              class="pl-3"
            >
              <v-switch
                v-model="countryFinanceModel.is_partner_sales_available"
                class="pa-0 ma-0"
                dense
                label="Partner sales available"
                hide-details
                @click.prevent.stop
              />
            </v-col>
            <v-col
              style="flex: 0 0 150px"
              class="text-right"
            >
              <v-chip
                color="primary"
                dark
                small
              >
                Profit: <strong class="ml-1">{{ countryFinanceModel.finance_model_calculation.navigare.profit.total_profit | currency(yachtModel.currency) }}</strong>
              </v-chip>
            </v-col>
            <v-col
              cols="auto"
              class="pl-4"
            >
              <v-icon @click.stop="countryFinanceModel.is_expanded = !countryFinanceModel.is_expanded">
                {{ countryFinanceModel.is_expanded ? 'expand_less' : 'expand_more' }}
              </v-icon>
            </v-col>
          </v-row>
          <div
            v-if="countryFinanceModel.finance_model.note"
            class="caption grey--text mt-2"
          >
            {{ countryFinanceModel.finance_model.note }}
          </div>
          <div class="mt-2">
            <v-chip
              v-for="param in countryFinanceModel.finance_model.parameter_values"
              :key="param.field"
              outlined
              small
            >
              {{ param.label }}:
              <strong class="ml-2">
                {{ param.value }} {{ param.unit }}
              </strong>
            </v-chip>
          </div>
          <v-expand-transition>
            <div v-if="countryFinanceModel.is_expanded">
              <v-card-text>
                <v-row no-gutters>
                  <v-col class="pr-2">
                    <div class="mb-2">
                      Investor Calculation
                    </div>
                    <v-card class="v-card--bordered">
                      <v-card-text class="py-1 px-4">
                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.sale_price"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Yacht Price
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.investor.sale_price | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.first_payment"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            First Payment
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.investor.first_payment_percentage | percentage }}
                            <span class="grey--text">
                              ({{ countryFinanceModel.finance_model_calculation.investor.first_payment | currency(yachtModel.currency) }})
                            </span>
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.last_payment"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Last Payment
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.investor.last_payment_percentage | percentage }}
                            <span class="grey--text">
                              ({{ countryFinanceModel.finance_model_calculation.investor.last_payment | currency(yachtModel.currency) }})
                            </span>
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.investment_period_months"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Investment period
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.investor.investment_period_months / 12 }} years
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.guaranteed_yearly_income"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Guaranteed yearly income
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            <span
                              v-if="countryFinanceModel.finance_model_calculation.investor.guaranteed_yearly_income_percentage"
                              class="grey--text"
                            >
                              ({{ countryFinanceModel.finance_model_calculation.investor.guaranteed_yearly_income_percentage | percentage }})
                            </span>
                            {{ countryFinanceModel.finance_model_calculation.investor.guaranteed_yearly_income | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <div v-if="countryFinanceModel.finance_model_calculation.investor.guaranteed_incomes">
                          <v-row
                            v-for="yearsIncome in countryFinanceModel.finance_model_calculation.investor.guaranteed_incomes"
                            :key="'yearly_income_' + yearsIncome.start"
                            align="center"
                            no-gutters
                            class="list--bordered py-2"
                          >
                            <v-col
                              class="grey--text text--darken-2"
                            >
                              Guaranteed yearly income - years {{ yearsIncome.start }} - {{ yearsIncome.end }}
                            </v-col>
                            <v-col
                              cols="auto"
                              class="text-right subtitle-1"
                            >
                              <div
                                class="text-no-wrap"
                              >
                                <span
                                  v-if="yearsIncome.guaranteed_yearly_income_percentage"
                                  class="grey--text"
                                >
                                  ({{ yearsIncome.guaranteed_yearly_income_percentage | percentage(1) }})
                                </span>
                                {{ yearsIncome.guaranteed_yearly_income | currency(yachtModel.currency) }}
                              </div>
                            </v-col>
                          </v-row>
                        </div>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.estimated_yearly_income"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Estimated yearly income
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.investor.estimated_yearly_income | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.estimated_yearly_expense"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Estimated yearly cost
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.investor.estimated_yearly_expense | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.investor.estimated_yearly_net_income"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            <strong>Estimated net yearly income</strong>
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            <strong>
                              <span
                                v-if="countryFinanceModel.finance_model_calculation.investor.estimated_yearly_net_income_percentage"
                                class="grey--text"
                              >
                                ({{ countryFinanceModel.finance_model_calculation.investor.estimated_yearly_net_income_percentage | percentage(1) }})
                              </span>
                              {{ countryFinanceModel.finance_model_calculation.investor.estimated_yearly_net_income | currency(yachtModel.currency) }}
                            </strong>
                          </v-col>
                        </v-row>

                        <div v-if="countryFinanceModel.finance_model_calculation.investor.additional_income">
                          <v-row
                            v-for="income in countryFinanceModel.finance_model_calculation.investor.additional_income"
                            :key="income.year"
                            align="center"
                            no-gutters
                            class="list--bordered py-2"
                          >
                            <v-col
                              cols="11"
                              class="grey--text text--darken-2"
                            >
                              Guaranteed income year {{ income.year }}
                            </v-col>
                            <v-col
                              cols="5"
                              class="text-right subtitle-1"
                            >
                              <span class="grey--text">
                                ({{ income.guaranteed_income_percentage | percentage(1) }})
                              </span>
                              {{ income.guaranteed_income | currency(yachtModel.currency) }}
                            </v-col>
                          </v-row>
                        </div>
                      </v-card-text>
                    </v-card>
                  </v-col>
                  <v-col class="pl-2">
                    <div class="mb-2">
                      Company Calculation
                    </div>
                    <v-card class="v-card--bordered">
                      <v-card-text class="py-1 px-4">
                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.sale_price"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.type === 'lease' ? 'Financed Amount' : 'Sale Price' }}
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.sale_price | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.financing_amount"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Financed Amount (incl. down payment)
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.financing_amount | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.purchase_price"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Purchase Price
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.purchase_price | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.other_costs"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Other Costs: {{ countryFinanceModel.finance_model_calculation.navigare.other_costs_description }}
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.other_costs | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.down_payment"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Down Payment
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            <span class="grey--text">
                              ({{ countryFinanceModel.finance_model_calculation.navigare.down_payment_percentage | percentage }})
                            </span>
                            {{ countryFinanceModel.finance_model_calculation.navigare.down_payment | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.last_payment"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Last Payment
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            <span class="grey--text">
                              ({{ countryFinanceModel.finance_model_calculation.navigare.last_payment_percentage | percentage }})
                            </span>
                            {{ countryFinanceModel.finance_model_calculation.navigare.last_payment | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.insurance"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Insurance
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            <span class="grey--text">
                              ({{ countryFinanceModel.finance_model_calculation.navigare.insurance_percentage | percentage }})
                            </span>
                            {{ countryFinanceModel.finance_model_calculation.navigare.insurance | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.support_amount"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Support to cover yearly loss
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.support_amount | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.monthly_payment"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Monthly Lease Annuity
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.monthly_payment | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Estimated Yearly Income
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.estimated_yearly_income | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Estimated Yearly Expense
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.estimated_yearly_expense | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          v-if="countryFinanceModel.finance_model_calculation.navigare.estimated_net_yearly_income"
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Estimated Net Yearly Income
                            <br>
                            <div class="caption grey--text">
                              Adjusted for income decrease during investment period
                            </div>
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.estimated_net_yearly_income | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Average Yearly Profit<br>
                            <div class="caption grey--text">
                              Net yearly yacht income - financing payments
                            </div>
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.profit.yearly_profit | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          align="center"
                          no-gutters
                          class="list--bordered py-2"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Initial Profit<br>
                            <div class="caption grey--text">
                              First payment from investor/leasing - any initial expenses
                            </div>
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.profit.initial | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>

                        <v-row
                          align="center"
                          no-gutters
                          class="list--bordered py-2 font-weight-bold"
                        >
                          <v-col
                            cols="11"
                            class="grey--text text--darken-2"
                          >
                            Total Profit
                          </v-col>
                          <v-col
                            cols="5"
                            class="text-right subtitle-1 font-weight-bold"
                          >
                            {{ countryFinanceModel.finance_model_calculation.navigare.profit.total_profit | currency(yachtModel.currency) }}
                          </v-col>
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>
              </v-card-text>
            </div>
          </v-expand-transition>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { sortBy } from 'lodash'
import YachtModel from '@/models/YachtModel'

export default {
  name: 'YachtModelDetailsFinanceModels',
  props: {
    yachtModel: {
      type: Object,
      default: () => {
      }
    }
  },
  data() {
    return {
      data: [],
      loading: false
    }
  },
  computed: {
    countries() {
      return sortBy(this.$store.getters['settings/charteringCountries'].data, 'name').map(c => {
        let yachtModelCountry = this.yachtModel.countries.find(pc => c.id === pc.id)

        return {
          ...c,
          included: !!yachtModelCountry,
          yachtModel: yachtModelCountry
        }
      })
    }
  },
  created() {
    this.fetchYachtModelFinanceModels()
  },
  methods: {
    async fetchYachtModelFinanceModels() {
      this.loading = true

      try {
        const result = await YachtModel.api.fetchAllFinanceModels(this.yachtModel.id)

        let models = result.map(m => {
          return {
            ...m,
            is_expanded: false
          }
        })

        this.data = models
      } catch (e) {
        this.$store.dispatch('alert', {
          text: 'Something went wrong',
          color: 'error'
        })
        throw e
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
